import React, { useState, useMemo, useContext, useCallback, useRef, useEffect } from "react";
import Editor from "@monaco-editor/react";
import NavBar from "../partials/NavBar";
import { GlobalContext } from "../GlobalContext";

const STORAGE_KEY = "l2dd_state";
const SEP = "␟"; // edits-map key separator (won't appear in data)

// Known id-like keys, in preference order, used to match new rows to old rows.
const ID_KEYS = [
  "quest_id",
  "skill_id",
  "npc_id",
  "item_id",
  "set_id",
  "hunting_zone_id",
  "id",
];

// Parse one tab-separated DAT line into tokens. "KEY=[VALUE]" / "KEY=VALUE";
// a token with no "=" (e.g. "newquestdialog_begin") is a marker.
function parseTokens(line) {
  return line.split("\t").map((raw) => {
    const eq = raw.indexOf("=");
    if (eq === -1) return { raw, key: raw, value: raw, wrapped: false, marker: true };
    const key = raw.substring(0, eq);
    let v = raw.substring(eq + 1);
    let wrapped = false;
    if (v.length >= 2 && v.startsWith("[") && v.endsWith("]")) {
      wrapped = true;
      v = v.slice(1, -1);
    }
    return { raw, key, value: v, wrapped, marker: false };
  });
}

function parseData(text) {
  return (text || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length)
    .map((l) => {
      const tokens = parseTokens(l);
      const byKey = {};
      tokens.forEach((t) => {
        if (!t.marker) byKey[t.key] = t;
      });
      return { tokens, byKey };
    });
}

// The name of the id column, from the first row.
function detectKeyName(rows) {
  if (!rows.length) return null;
  const keys = rows[0].tokens.filter((t) => !t.marker).map((t) => t.key);
  for (const idk of ID_KEYS) {
    const hit = keys.find((k) => k.toLowerCase() === idk);
    if (hit) return hit;
  }
  const suffix = keys.find((k) => /(^|_)id$/i.test(k));
  return suffix || keys[0] || null;
}

// Make a value readable: <br> and literal \n become real newlines.
function toView(v) {
  return (v || "").replace(/<br\s*\/?>/gi, "\n").replace(/\\n/g, "\n");
}

function previewOf(v) {
  const view = toView(v);
  const first = view.split("\n")[0];
  const multi = view.includes("\n");
  const clipped = first.length > 70 ? first.slice(0, 70) + "…" : first;
  return clipped + (multi ? " ⏎" : "");
}

// LCS line diff, with adjacent remove+add merged into a single "changed" row for
// a clean side-by-side view.
function lineDiff(oldText, newText) {
  const a = (oldText || "").split("\n");
  const b = (newText || "").split("\n");
  const m = a.length;
  const n = b.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const raw = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      raw.push({ type: "same", old: a[i], new: b[j] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      raw.push({ type: "removed", old: a[i], new: null });
      i++;
    } else {
      raw.push({ type: "added", old: null, new: b[j] });
      j++;
    }
  }
  while (i < m) raw.push({ type: "removed", old: a[i++], new: null });
  while (j < n) raw.push({ type: "added", old: null, new: b[j++] });

  // Pair a run of removed lines with the following run of added lines
  // POSITIONALLY (removed[p] <-> added[p]) so several consecutive changed lines
  // align 1:1 instead of collapsing into one mispaired block.
  const merged = [];
  let k = 0;
  while (k < raw.length) {
    if (raw[k].type === "removed") {
      const rem = [];
      while (k < raw.length && raw[k].type === "removed") rem.push(raw[k++]);
      const add = [];
      while (k < raw.length && raw[k].type === "added") add.push(raw[k++]);
      const pairs = Math.min(rem.length, add.length);
      for (let p = 0; p < pairs; p++) {
        merged.push({ type: "changed", old: rem[p].old, new: add[p].new });
      }
      for (let p = pairs; p < rem.length; p++) merged.push(rem[p]);
      for (let p = pairs; p < add.length; p++) merged.push(add[p]);
    } else {
      merged.push(raw[k++]);
    }
  }
  return merged;
}

// Character-level diff between two strings (LCS), returning runs of
// {type: 'eq'|'del'|'ins', text}. Skips (returns null) for very long strings so
// we don't build a huge DP matrix.
function charDiff(a, b) {
  const n = a.length;
  const m = b.length;
  if (n * m > 4000000) return null;
  const dp = Array.from({ length: n + 1 }, () => new Int32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const ops = [];
  const push = (type, ch) => {
    const last = ops[ops.length - 1];
    if (last && last.type === type) last.text += ch;
    else ops.push({ type, text: ch });
  };
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      push("eq", a[i]);
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      push("del", a[i]);
      i++;
    } else {
      push("ins", b[j]);
      j++;
    }
  }
  while (i < n) push("del", a[i++]);
  while (j < m) push("ins", b[j++]);
  return ops;
}

// Build the {old,new} span lists for one aligned diff row, with inline
// (character-level) highlighting on changed lines.
function rowSpans(d) {
  if (d.type === "same") {
    return { oldSpans: [{ cls: "", text: d.old }], newSpans: [{ cls: "", text: d.new }] };
  }
  if (d.type === "removed") {
    return { oldSpans: [{ cls: "dd-seg-del", text: d.old }], newSpans: [] };
  }
  if (d.type === "added") {
    return { oldSpans: [], newSpans: [{ cls: "dd-seg-ins", text: d.new }] };
  }
  // changed: character diff between the two lines
  const ops = charDiff(d.old || "", d.new || "");
  if (!ops) {
    return {
      oldSpans: [{ cls: "dd-seg-del", text: d.old }],
      newSpans: [{ cls: "dd-seg-ins", text: d.new }],
    };
  }
  const oldSpans = [];
  const newSpans = [];
  for (const op of ops) {
    if (op.type === "eq") {
      oldSpans.push({ cls: "", text: op.text });
      newSpans.push({ cls: "", text: op.text });
    } else if (op.type === "del") {
      oldSpans.push({ cls: "dd-seg-del", text: op.text });
    } else {
      newSpans.push({ cls: "dd-seg-ins", text: op.text });
    }
  }
  return { oldSpans, newSpans };
}

function loadState() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch (e) {
    return {};
  }
}

function DataDiff() {
  const { username, logout, updateDatabase } = useContext(GlobalContext);

  const init = useRef(loadState()).current;
  const [rawNew, setRawNew] = useState(init.rawNew || "");
  const [rawOld, setRawOld] = useState(init.rawOld || "");
  const [edits, setEdits] = useState(init.edits || {});
  const [keyOverride, setKeyOverride] = useState(init.keyOverride || null);
  const [sel, setSel] = useState(null); // {rowIdx, colKey, keyVal}
  const [editValue, setEditValue] = useState("");
  const editRef = useRef("");
  const commitTimer = useRef(null);

  const newRows = useMemo(() => parseData(rawNew), [rawNew]);
  const oldRows = useMemo(() => parseData(rawOld), [rawOld]);

  const detectedKey = useMemo(() => detectKeyName(newRows), [newRows]);
  const keyName = keyOverride || detectedKey;

  const allKeys = useMemo(
    () => (newRows[0] ? newRows[0].tokens.filter((t) => !t.marker).map((t) => t.key) : []),
    [newRows]
  );

  // Column template from the first new row (markers included, in order).
  const columns = useMemo(() => {
    if (!newRows[0]) return [];
    return newRows[0].tokens.map((t, i) => ({
      idx: i,
      key: t.marker ? `__m${i}` : t.key,
      label: t.marker ? t.raw : t.key,
      marker: t.marker,
    }));
  }, [newRows]);

  const oldByKey = useMemo(() => {
    const map = new Map();
    if (!keyName) return map;
    for (const row of oldRows) {
      const kv = row.byKey[keyName] ? row.byKey[keyName].value : undefined;
      if (kv != null && !map.has(kv)) map.set(kv, row);
    }
    return map;
  }, [oldRows, keyName]);

  const rowKeyVal = useCallback(
    (row, idx) => (keyName && row.byKey[keyName] ? row.byKey[keyName].value : "#" + idx),
    [keyName]
  );

  const getCellValue = useCallback(
    (rowIdx, colKey) => {
      const row = newRows[rowIdx];
      if (!row) return "";
      const ek = rowKeyVal(row, rowIdx) + SEP + colKey;
      if (ek in edits) return edits[ek];
      return row.byKey[colKey] ? row.byKey[colKey].value : "";
    },
    [newRows, edits, rowKeyVal]
  );

  const getOldValue = useCallback(
    (rowIdx, colKey) => {
      const row = newRows[rowIdx];
      if (!row) return null;
      const match = oldByKey.get(rowKeyVal(row, rowIdx));
      if (!match) return null;
      return match.byKey[colKey] ? match.byKey[colKey].value : null;
    },
    [newRows, oldByKey, rowKeyVal]
  );

  // Persist everything.
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ rawNew, rawOld, edits, keyOverride })
        );
      } catch (e) {
        /* ignore */
      }
    }, 300);
    return () => clearTimeout(t);
  }, [rawNew, rawOld, edits, keyOverride]);

  // The editor holds the RAW value (literal <br> / \n), so an edit is stored
  // verbatim — line breaks are only rendered as breaks in the diff view.
  const commit = useCallback((selObj, value) => {
    if (!selObj) return;
    const k = selObj.keyVal + SEP + selObj.colKey;
    setEdits((prev) => {
      if (prev[k] === value) return prev;
      return { ...prev, [k]: value };
    });
  }, []);

  const selectCell = (rowIdx, col) => {
    if (col.marker) return;
    if (sel) commit(sel, editRef.current);
    const row = newRows[rowIdx];
    const keyVal = rowKeyVal(row, rowIdx);
    const value = getCellValue(rowIdx, col.key);
    const newSel = { rowIdx, colKey: col.key, keyVal };
    setSel(newSel);
    setEditValue(value);
    editRef.current = value;
  };

  const onEditChange = (v) => {
    const val = v == null ? "" : v;
    setEditValue(val);
    editRef.current = val;
    clearTimeout(commitTimer.current);
    const s = sel;
    commitTimer.current = setTimeout(() => commit(s, val), 250);
  };

  const revertCell = () => {
    if (!sel) return;
    const k = sel.keyVal + SEP + sel.colKey;
    setEdits((prev) => {
      const next = { ...prev };
      delete next[k];
      return next;
    });
    const row = newRows[sel.rowIdx];
    const orig = row && row.byKey[sel.colKey] ? row.byKey[sel.colKey].value : "";
    setEditValue(orig);
    editRef.current = orig;
  };

  const buildOutput = useCallback(() => {
    // Apply the in-flight edit so Copy always reflects the editor.
    const live = { ...edits };
    if (sel) live[sel.keyVal + SEP + sel.colKey] = editRef.current;
    return newRows
      .map((row, rowIdx) => {
        const kv = rowKeyVal(row, rowIdx);
        return row.tokens
          .map((t) => {
            if (t.marker) return t.raw;
            const ek = kv + SEP + t.key;
            const value = ek in live ? live[ek] : t.value;
            return `${t.key}=${t.wrapped ? `[${value}]` : value}`;
          })
          .join("\t");
      })
      .join("\n");
  }, [newRows, edits, sel, rowKeyVal]);

  const copyResult = async () => {
    try {
      await navigator.clipboard.writeText(buildOutput());
    } catch (e) {
      /* clipboard blocked */
    }
  };

  const clearAll = () => {
    setRawNew("");
    setRawOld("");
    setEdits({});
    setKeyOverride(null);
    setSel(null);
    setEditValue("");
    editRef.current = "";
  };

  const changedCount = useMemo(() => {
    let n = 0;
    for (let r = 0; r < newRows.length; r++) {
      for (const col of columns) {
        if (col.marker) continue;
        const nv = getCellValue(r, col.key);
        const ov = getOldValue(r, col.key);
        if (ov != null && ov !== nv) n++;
      }
    }
    return n;
  }, [newRows, columns, getCellValue, getOldValue]);

  const diffRows = useMemo(() => {
    if (!sel) return [];
    const oldView = toView(getOldValue(sel.rowIdx, sel.colKey) || "");
    return lineDiff(oldView, toView(editValue)).map((d) => ({ ...d, ...rowSpans(d) }));
  }, [sel, editValue, getOldValue]);

  const hasData = newRows.length > 0;

  return (
    <div>
      <NavBar username={username} onLogout={logout} updateDatabase={updateDatabase} />
      <div className="dd-page">
        <h2 className="dd-title">Data Diff</h2>

        {!hasData ? (
          <>
            <p className="dd-sub">
              Paste the <b>new</b> client data (only the new/changed rows) and the <b>old</b> data
              (everything). Rows are matched by their id column. Then click any field to compare and
              edit it.
            </p>
            <div className="dd-inputs">
              <label className="dd-inp">
                <span>New data</span>
                <textarea
                  value={rawNew}
                  onChange={(e) => setRawNew(e.target.value)}
                  placeholder="Paste new rows (one record per line, tab-separated tokens)…"
                />
              </label>
              <label className="dd-inp">
                <span>Old data</span>
                <textarea
                  value={rawOld}
                  onChange={(e) => setRawOld(e.target.value)}
                  placeholder="Paste the full old dataset here…"
                />
              </label>
            </div>
          </>
        ) : (
          <>
            <div className="dd-toolbar">
              <span className="dd-meta">
                {newRows.length} new row{newRows.length === 1 ? "" : "s"} · {oldRows.length} old ·{" "}
                {changedCount} changed field{changedCount === 1 ? "" : "s"}
              </span>
              <label className="dd-ctl">
                Match by
                <select value={keyName || ""} onChange={(e) => setKeyOverride(e.target.value)}>
                  {allKeys.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </label>
              <button className="dd-btn" onClick={copyResult}>
                Copy full result
              </button>
              <button className="dd-btn dd-btn-ghost" onClick={clearAll}>
                Clear
              </button>
            </div>

            <div className="dd-gridwrap">
              <table className="dd-grid">
                <thead>
                  <tr>
                    <th className="dd-rowhdr">#</th>
                    {columns.map((col) => (
                      <th key={col.key} className={col.marker ? "dd-col-marker" : ""}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {newRows.map((row, rowIdx) => {
                    const matched = oldByKey.has(rowKeyVal(row, rowIdx));
                    return (
                      <tr key={rowIdx} className={matched ? "" : "dd-row-new"}>
                        <td className="dd-rowhdr">{rowIdx + 1}</td>
                        {columns.map((col) => {
                          if (col.marker) {
                            return (
                              <td key={col.key} className="dd-cell dd-col-marker">
                                {col.label}
                              </td>
                            );
                          }
                          const nv = getCellValue(rowIdx, col.key);
                          const ov = getOldValue(rowIdx, col.key);
                          const isSel = sel && sel.rowIdx === rowIdx && sel.colKey === col.key;
                          const edited =
                            rowKeyVal(row, rowIdx) + SEP + col.key in edits;
                          let cls = "dd-cell";
                          if (!matched || ov == null) cls += " dd-cell-added";
                          else if (ov !== nv) cls += " dd-cell-changed";
                          if (edited) cls += " dd-cell-edited";
                          if (isSel) cls += " dd-cell-sel";
                          return (
                            <td
                              key={col.key}
                              className={cls}
                              onClick={() => selectCell(rowIdx, col)}
                              title={col.key}
                            >
                              {previewOf(nv) || <span className="dd-empty">(empty)</span>}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {sel ? (
              <div className="dd-detail">
                <div className="dd-detail-head">
                  <b>{sel.keyVal}</b> · <span className="dd-field">{sel.colKey}</span>
                  <button className="dd-btn dd-btn-ghost dd-revert" onClick={revertCell}>
                    Revert cell
                  </button>
                </div>

                <div className="dd-diff">
                  <div className="dd-diff-col dd-diff-old">
                    <div className="dd-diff-h">Old</div>
                    {getOldValue(sel.rowIdx, sel.colKey) == null ? (
                      <div className="dd-diff-none">No matching old record.</div>
                    ) : (
                      diffRows.map((d, i) => (
                        <div key={i} className={"dd-line dd-line-" + d.type}>
                          {d.oldSpans.length === 0 ? " " : d.oldSpans.map((s, k) => (<span key={k} className={s.cls}>{s.text}</span>))}
                        </div>
                      ))
                    )}
                  </div>
                  <div className="dd-diff-col dd-diff-new">
                    <div className="dd-diff-h">New</div>
                    {diffRows.map((d, i) => (
                      <div key={i} className={"dd-line dd-line-" + d.type}>
                        {d.newSpans.length === 0 ? " " : d.newSpans.map((s, k) => (<span key={k} className={s.cls}>{s.text}</span>))}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="dd-editor">
                  <div className="dd-editor-h">Edit (line breaks shown as newlines)</div>
                  <Editor
                    height="240px"
                    theme="vs-dark"
                    language="plaintext"
                    value={editValue}
                    onChange={onEditChange}
                    options={{
                      wordWrap: "on",
                      minimap: { enabled: false },
                      fontSize: 13,
                      lineNumbers: "on",
                      scrollBeyondLastLine: false,
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="dd-hint">Click a field above to compare and edit it.</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default DataDiff;
